import { UIElement } from '../ui-element.js';
import { BrandColors } from '../../global/helpers-marketing.js';
import styles from './ui-salesribbon.css';
import { element } from '../../global/template-literals.js';
import { setAttributes } from '../../global/render-api.js';
import { UIIcon } from '../icon/ui-icon.js';

/**
 * @memberof SharedComponents
 * @augments {UIElement}
 * @alias UISalesRibbon
 * @element ui-salesribbon
 * @classdesc Represents a class for <code>ui-salesribbon</code> element.
 * Marketing element for sales ribbon.
 * @property {UIIconGlyph} [icon] {@attr icon} Product icon glyph like in ui-icon.
 * @property {UISalesRibbonIconType} [iconType="product"] {@attr icon-type}
 *  {@desc product}
 *  {@desc image}
 *  {@desc badge}
 * @property {UIBrandColor | 'yellow'} [badgeColor="turquoise"] {@attr badge-color}
 * @property {('white' | 'orange' | 'turquoise' | 'lilac')} [glyphColor="white"] {@attr glyph-color}
 * Available only for the icon type product. Uses under the hood the <ui-icon size="avatar"></ui-icon>
 * @slot
 * @example
 * <ui-salesribbon icon="calculator" icon-type="badge">
 *   The best offer!
 * </ui-salesribbon>
 */
class UISalesRibbon extends UIElement {
    /**
     * @type {UISalesRibbonIconTypes}
     */
    static get IconTypes() {
        return {
            Product: 'product',
            Image: 'image',
            Badge: 'badge',
        };
    }

    /**
     * @type {IProps}
     * @readonly
     */
    static get props() {
        return {
            attributes: {
                icon: String,
                iconType: {
                    type: String,
                    default: this.IconTypes.Product,
                },
                glyphColor: {
                    type: String,
                    default: UIIcon.colors.WHITE,
                },
                badgeColor: {
                    type: String,
                    default: BrandColors.Turquoise,
                },
            },
        };
    }

    /**
     * @private
     * @returns {HTMLDivElement | null}
     */
    getIConElement() {
        if (!this.icon) {
            return null;
        }

        const isIconBadge = this.iconType === this.constructor.IconTypes.Badge;
        const badgeElement = isIconBadge
            ? element`<ui-badge color="${this.badgeColor}"></ui-badge>`
            : null;
        const isImageIcon = this.iconType === this.constructor.IconTypes.Image;
        const iconElement = isImageIcon
            ? element`<ui-icon src="${this.icon}"></ui-icon>`
            : element`<ui-icon glyph="${this.icon}"></ui-icon>`;

        if (badgeElement) {
            badgeElement.append(iconElement);
        } else if (!isImageIcon) {
            setAttributes(iconElement, {
                size: 'avatar',
                ...(this.glyphColor ? { color: this.glyphColor } : undefined),
                bgcolor: this.badgeColor,
            });
        }

        return element`<div class="ui-salesribbon__icon-wrapper">${badgeElement || iconElement}</div>`;
    }

    /**
     * @inheritDoc
     */
    render() {
        const elements = [];
        const iconElement = this.getIConElement();
        const childrenElements = Array.from(this.childNodes).filter(
            (node) =>
                node.nodeType === Node.ELEMENT_NODE ||
                node.nodeValue.trim().length > 0
        );
        const linkOutElement =
            childrenElements[
                Math.max(childrenElements.length - 1, 0)
            ]?.tagName?.toLowerCase() === 'a'
                ? childrenElements.pop()
                : null;
        const areInlineChildrenElements = childrenElements.reduce(
            (isInline, childNode) =>
                childNode.nodeType === Node.TEXT_NODE ||
                childNode.tagName?.toLowerCase() === 'span',
            true
        );

        iconElement && elements.push(iconElement);
        childrenElements.length > 0 &&
            elements.push(
                element`<div class="ui-salesribbon__message${areInlineChildrenElements ? ' ui-salesribbon__simple-message' : ''}">${childrenElements}</div>`
            );
        linkOutElement &&
            elements.push(
                element`<div class="ui-salesribbon__link-wrapper">${linkOutElement}</div>`
            );
        this.append(...elements);
    }
}

UISalesRibbon.defineElement('ui-salesribbon', styles);
export { UISalesRibbon };
